import { useState, useMemo } from "react";
import { getStageConfig } from "@/data/workflowEngine";
import { useAppData } from "@/data/AppDataContext";
import { useCatalog } from "@/data/CatalogContext";
import { useProductRegistry } from "@/data/ProductRegistryContext";
import { JobService } from "@/services/job.service";
import { replaceJobMaterialsSnapshot } from "@/services/inventory.service";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import { toast } from "sonner";
import {
  Package, CheckCircle, ClipboardList, Eye, Ruler, Camera, X,
} from "lucide-react";
import type { Job } from "@/data/types";

/* ── Helpers ── */
function getProductLabel(job: Job): string {
  const material = job.projectConfig?.bathroomMaterials?.[0] ?? job.projectConfig?.primaryMaterial ?? "";
  const type = job.projectType || "Shower";
  return [material, type].filter(Boolean).join(" ");
}

function withFallbackProjectConfig(job: Job, quotes: { linkedJobId?: string; projectConfig?: Job["projectConfig"] }[]): Job {
  if (job.projectConfig) return job;
  const linkedQuote = quotes.find((quote) => (
    quote.projectConfig &&
    (
      quote.linkedJobId === job.id ||
      (job.convertedFromQuoteId && "linkedQuoteId" in quote && quote.linkedQuoteId === job.convertedFromQuoteId) ||
      (job.convertedFromQuoteId && "id" in quote && quote.id === job.convertedFromQuoteId)
    )
  ));
  return linkedQuote?.projectConfig ? { ...job, projectConfig: linkedQuote.projectConfig } : job;
}

interface MaterialLine {
  name: string;
  sku: string;
  qty: number;
  category: string;
  unitCost: number;
  unitPrice?: number;
}

function normalizeMaterialKey(value: string | null | undefined): string {
  return (value || "")
    .trim()
    .toLowerCase()
    .replace(/[\s_-]+/g, " ")
    .replace(/[^\w\s]/g, "");
}

function parsePricingImpactAmount(pricingImpact: string | null | undefined): number {
  const raw = (pricingImpact || "").trim();
  if (!raw) return 0;
  const match = raw.match(/([-+]?)\s*\$?\s*([\d,]+(?:\.\d+)?)/);
  if (!match) return 0;
  const amount = Number(match[2].replace(/,/g, ""));
  if (!Number.isFinite(amount)) return 0;
  return amount > 0 ? amount : 0;
}

interface MaterialResolution {
  name: string;
  sku: string;
  unitCost: number;
  unitPrice?: number;
}

type MaterialResolver = (value: string, fallbackSku: string) => MaterialResolution;

function deriveMaterials(job: Job, resolveMaterial: MaterialResolver): MaterialLine[] {
  const items: MaterialLine[] = [];
  if (!job.projectConfig) return items;
  const cfg = job.projectConfig;

  if (cfg.primaryMaterial) {
    const resolved = resolveMaterial(`${cfg.primaryMaterial} Wall System`, "WALL-001");
    items.push({ name: resolved.name, sku: resolved.sku, qty: 1, category: "WALL", unitCost: resolved.unitCost, unitPrice: resolved.unitPrice });
  }
  if (cfg.selectedBase) {
    const resolved = resolveMaterial(cfg.selectedBase, "BASE-001");
    items.push({ name: resolved.name, sku: resolved.sku, qty: 1, category: "BASE", unitCost: resolved.unitCost, unitPrice: resolved.unitPrice });
  }
  if (cfg.enclosureType) {
    const resolved = resolveMaterial(cfg.enclosureType, "ENC-001");
    items.push({ name: resolved.name, sku: resolved.sku, qty: 1, category: "ENC", unitCost: resolved.unitCost, unitPrice: resolved.unitPrice });
  }
  if (cfg.hardwareFinish) {
    const resolved = resolveMaterial(`${cfg.hardwareFinish} Hardware Kit`, "HW-001");
    items.push({ name: resolved.name, sku: resolved.sku, qty: 1, category: "HW", unitCost: resolved.unitCost, unitPrice: resolved.unitPrice });
  }
  if (cfg.selectedDoor) {
    const resolved = resolveMaterial(cfg.selectedDoor, "DOOR-001");
    items.push({ name: resolved.name, sku: resolved.sku, qty: 1, category: "DOOR", unitCost: resolved.unitCost, unitPrice: resolved.unitPrice });
  }
  if (cfg.selectedFixture) {
    const resolved = resolveMaterial(cfg.selectedFixture, "FIX-001");
    items.push({ name: resolved.name, sku: resolved.sku, qty: 1, category: "FIX", unitCost: resolved.unitCost, unitPrice: resolved.unitPrice });
  }
  cfg.selectedAddOns?.forEach(a => {
    const fallbackSku = `ADD-${a.substring(0, 3).toUpperCase()}`;
    const resolved = resolveMaterial(a, fallbackSku);
    items.push({ name: resolved.name, sku: resolved.sku, qty: 1, category: "ADD", unitCost: resolved.unitCost, unitPrice: resolved.unitPrice });
  });
  cfg.customAddOns?.forEach(a => {
    const fallbackSku = `CUST-${a.name.substring(0, 3).toUpperCase()}`;
    const override = Number(a.priceOverride);
    const resolved = resolveMaterial(a.name, fallbackSku);
    items.push({ name: resolved.name, sku: resolved.sku, qty: 1, category: "CUSTOM", unitCost: Number.isFinite(override) && override > 0 ? override : resolved.unitCost, unitPrice: resolved.unitPrice });
  });
  return items;
}

function toJobMaterialLines(items: MaterialLine[]): NonNullable<Job["materialsList"]> {
  return items.map((item) => ({
    sku: item.sku,
    itemName: item.name,
    quantityRequired: item.qty,
    quantityAllocated: 0,
    quantityStaged: 0,
    quantityInstalled: 0,
    missingItems: 0,
    unitCost: item.unitCost,
    unitPrice: item.unitPrice,
    fieldPurchase: false,
    inventoryStatus: "Allocated",
    inventoryAssignment: "Assigned",
    lifecycleStage: "Assigned",
  }));
}

interface MaterialPlanningPanelProps {
  onConfirmed?: (job: Job) => void;
}

export function MaterialPlanningPanel({ onConfirmed }: MaterialPlanningPanelProps) {
  const { jobs, setJobs, quotes } = useAppData();
  const { products: catalogProducts } = useCatalog();
  const { products: registryProducts } = useProductRegistry();
  const [reviewJobId, setReviewJobId] = useState<string | null>(null);

  const catalogById = useMemo(() => {
    const map = new Map<string, MaterialResolution>();
    catalogProducts.forEach((product) => {
      const productCostFromVariant = product.variants.find((variant) => (variant.cost ?? 0) > 0)?.cost ?? 0;
      const productCost = productCostFromVariant || parsePricingImpactAmount(product.pricingImpact);
      const productPriceFromVariant = product.variants.find((variant) => (variant.basePrice ?? 0) > 0)?.basePrice ?? 0;
      const productPrice = productPriceFromVariant || parsePricingImpactAmount(product.pricingImpact);
      const productSku = product.skus[0] || "";
      map.set(product.id, { name: product.displayName, sku: productSku, unitCost: productCost || 0, unitPrice: productPrice });

      product.variants.forEach((variant) => {
        const variantCost = variant.cost ?? productCost ?? 0;
        const variantPrice = variant.basePrice ?? productPrice ?? 0;
        map.set(variant.id, {
          name: `${product.displayName} — ${variant.name}`,
          sku: variant.sku || productSku,
          unitCost: variantCost || 0,
          unitPrice: variantPrice,
        });
      });
    });
    return map;
  }, [catalogProducts]);

  const catalogBySku = useMemo(() => {
    const map = new Map<string, MaterialResolution>();
    catalogProducts.forEach((product) => {
      const productCostFromVariant = product.variants.find((variant) => (variant.cost ?? 0) > 0)?.cost ?? 0;
      const productCost = productCostFromVariant || parsePricingImpactAmount(product.pricingImpact);
      const productPriceFromVariant = product.variants.find((variant) => (variant.basePrice ?? 0) > 0)?.basePrice ?? 0;
      const productPrice = productPriceFromVariant || parsePricingImpactAmount(product.pricingImpact);
      product.skus.forEach((sku) => {
        if (sku) {
          map.set(sku, { name: product.displayName, sku, unitCost: productCost || 0, unitPrice: productPrice });
        }
      });
      product.variants.forEach((variant) => {
        const variantCost = variant.cost ?? productCost ?? 0;
        const variantPrice = variant.basePrice ?? productPrice ?? 0;
        if (variant.sku) {
          map.set(variant.sku, {
            name: `${product.displayName} — ${variant.name}`,
            sku: variant.sku,
            unitCost: variantCost || 0,
            unitPrice: variantPrice,
          });
        }
      });
    });
    return map;
  }, [catalogProducts]);

  const catalogByName = useMemo(() => {
    const map = new Map<string, MaterialResolution>();
    catalogProducts.forEach((product) => {
      const productCostFromVariant = product.variants.find((variant) => (variant.cost ?? 0) > 0)?.cost ?? 0;
      const productCost = productCostFromVariant || parsePricingImpactAmount(product.pricingImpact);
      const productPriceFromVariant = product.variants.find((variant) => (variant.basePrice ?? 0) > 0)?.basePrice ?? 0;
      const productPrice = productPriceFromVariant || parsePricingImpactAmount(product.pricingImpact);
      const productSku = product.skus[0] || "";
      map.set(normalizeMaterialKey(product.displayName), {
        name: product.displayName,
        sku: productSku,
        unitCost: productCost || 0,
        unitPrice: productPrice,
      });
      product.variants.forEach((variant) => {
        const variantCost = variant.cost ?? productCost ?? 0;
        const variantPrice = variant.basePrice ?? productPrice ?? 0;
        map.set(normalizeMaterialKey(variant.name), {
          name: `${product.displayName} — ${variant.name}`,
          sku: variant.sku || productSku,
          unitCost: variantCost || 0,
          unitPrice: variantPrice,
        });
        map.set(normalizeMaterialKey(`${product.displayName} ${variant.name}`), {
          name: `${product.displayName} — ${variant.name}`,
          sku: variant.sku || productSku,
          unitCost: variantCost || 0,
          unitPrice: variantPrice,
        });
      });
    });
    return map;
  }, [catalogProducts]);

   const registryById = useMemo(() => {
    const map = new Map<string, MaterialResolution>();
    registryProducts.forEach((product) => {
      if (product.productId) {
        map.set(product.productId, {
          name: product.productName,
          sku: product.sku || "",
          unitCost: Number(product.cost) || 0,
          unitPrice: Number(product.price) || 0,
        });
      }
    });
    return map;
  }, [registryProducts]);

  const registryBySku = useMemo(() => {
    const map = new Map<string, MaterialResolution>();
    registryProducts.forEach((product) => {
      if (product.sku) {
        map.set(product.sku, {
          name: product.productName,
          sku: product.sku,
          unitCost: Number(product.cost) || 0,
          unitPrice: Number(product.price) || 0,
        });
      }
    });
    return map;
  }, [registryProducts]);

  const registryByName = useMemo(() => {
    const map = new Map<string, MaterialResolution>();
    registryProducts.forEach((product) => {
      if (product.productName) {
        map.set(normalizeMaterialKey(product.productName), {
          name: product.productName,
          sku: product.sku || "",
          unitCost: Number(product.cost) || 0,
          unitPrice: Number(product.price) || 0,
        });
      }
    });
    return map;
  }, [registryProducts]);

  const resolveMaterial: MaterialResolver = (value, fallbackSku) => {
    const raw = value.trim();
    const byId = catalogById.get(raw) || registryById.get(raw);
    if (byId) return { ...byId, sku: byId.sku || fallbackSku };

    const bySku = catalogBySku.get(raw) || registryBySku.get(raw);
    if (bySku) return { ...bySku, sku: bySku.sku || fallbackSku };

    const key = normalizeMaterialKey(raw);
    const byName = catalogByName.get(key) || registryByName.get(key);
    if (byName) return { ...byName, sku: byName.sku || fallbackSku };

    for (const [k, found] of [...catalogByName.entries(), ...registryByName.entries()]) {
      if (k.includes(key) || key.includes(k)) {
        return { ...found, sku: found.sku || fallbackSku };
      }
    }

    return { name: raw, sku: fallbackSku, unitCost: 0 };
  };

  const planningSourceJobs = useMemo(
    () => jobs.map((job) => withFallbackProjectConfig(job, quotes)),
    [jobs, quotes],
  );

  const planningJobs = useMemo(() =>
    planningSourceJobs.filter(j =>
      j.approvedForOrdering === true &&
      j.planningStatus === "In Planning" &&
      (getStageConfig(j.currentStage)?.order ?? 0) <= 25
    ),
  [planningSourceJobs]);

  const reviewJob = reviewJobId ? planningSourceJobs.find(j => j.id === reviewJobId) : null;
  const materials = reviewJob ? deriveMaterials(reviewJob, resolveMaterial) : [];

  const handleConfirmPlanning = (jobId: string) => {
    const sourceJob = planningSourceJobs.find((j) => j.id === jobId);
    const generatedMaterials = sourceJob ? toJobMaterialLines(deriveMaterials(sourceJob, resolveMaterial)) : [];

    setJobs(prev => prev.map(j => j.id !== jobId ? j : {
      ...j,
      planningStatus: "Ready for Ordering" as const,
      currentStage: "Ready for Ordering" as const,
      materialReadiness: generatedMaterials.length > 0 ? "Not Ordered" : j.materialReadiness,
      materialsList: generatedMaterials.length > 0 ? generatedMaterials : j.materialsList,
      activityHistory: [...j.activityHistory, {
        id: `act-plan-${Date.now()}`,
        date: new Date().toISOString(),
        action: "Material plan confirmed — ready for ordering",
        user: "Ops Manager",
        noteType: "ops" as const,
      }],
    }));
    JobService.update(jobId, {
      planningStatus: "Ready for Ordering",
      currentStage: "Ready for Ordering",
      materialReadiness: generatedMaterials.length > 0 ? "Not Ordered" : undefined,
      materialsList: generatedMaterials.length > 0 ? generatedMaterials : undefined,
    }).then(async ({ data }) => {
      if (data && generatedMaterials.length > 0) {
        await replaceJobMaterialsSnapshot({
          id: data.id,
          jobNumber: data.jobNumber,
          customerName: data.customerName,
          currentStage: data.currentStage,
          materialsList: generatedMaterials,
        });
      }
    }).catch((err) => {
      console.error("[MaterialPlanningPanel] Failed to persist planning confirmation:", err);
      toast.error("Failed to persist material planning update");
    });
    toast.success("Materials confirmed", { description: "Job is now ready for ordering" });
    setReviewJobId(null);
    if (sourceJob && onConfirmed) {
      onConfirmed(sourceJob);
    }
  };

  if (planningJobs.length === 0) return null;

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2.5">
        <Package className="h-5 w-5 text-primary" />
        <h2 className="text-[16px] font-bold tracking-tight text-foreground">Material Planning</h2>
        <Badge variant="secondary" className="text-[10px] h-5">
          {planningJobs.length} job{planningJobs.length !== 1 ? "s" : ""}
        </Badge>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
        {planningJobs.map(job => (
          <Card key={job.id} className="border border-primary/20 bg-primary/[0.02] shadow-none hover:-translate-y-[1px] hover:shadow-md transition-all">
            <CardContent className="px-4 py-3 space-y-2">
              <div className="flex items-center justify-between">
                <p className="text-[13px] font-bold text-foreground truncate">
                  {job.customerName} — {job.address?.city || "Unknown"}
                </p>
                <Badge className="text-[10px] bg-primary/15 text-primary border-0">In Planning</Badge>
              </div>
              <div className="flex items-center gap-3">
                <span className="text-[12px] text-muted-foreground">{getProductLabel(job)}</span>
                <div className="flex items-center gap-2 ml-auto">
                  <span className="inline-flex items-center gap-1 text-[10px] text-muted-foreground/60">
                    <Camera className="h-3 w-3" />
                    {job.photos?.length || 0}
                  </span>
                  <span className="inline-flex items-center gap-1 text-[10px] text-muted-foreground/60">
                    <Ruler className="h-3 w-3" />
                    {job.projectConfig?.totalSqFt ? "Yes" : "No"}
                  </span>
                </div>
              </div>
              <div className="flex items-center gap-2 pt-1">
                <Button size="sm" variant="outline" className="h-7 text-[11px] font-semibold gap-1"
                  onClick={() => setReviewJobId(job.id)}>
                  <Eye className="h-3 w-3" /> Review Materials
                </Button>
                <Button size="sm" className="h-7 text-[11px] font-semibold gap-1"
                  onClick={() => handleConfirmPlanning(job.id)}>
                  <CheckCircle className="h-3 w-3" /> Confirm & Ready
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Material Review Modal */}
      <Dialog open={!!reviewJobId} onOpenChange={o => { if (!o) setReviewJobId(null); }}>
        <DialogContent className="max-w-4xl w-[95vw] max-h-[90vh] p-0 gap-0">
          {reviewJob && (
            <div className="flex flex-col" style={{ maxHeight: "85vh" }}>
              {/* Header */}
              <div className="px-6 py-4 border-b border-border flex items-center justify-between">
                <div>
                  <h3 className="text-[16px] font-bold text-foreground flex items-center gap-2">
                    <Package className="h-4.5 w-4.5 text-primary" />
                    Material Planning — {reviewJob.customerName}
                  </h3>
                  <p className="text-[11px] text-muted-foreground mt-0.5">
                    {getProductLabel(reviewJob)} • {reviewJob.address?.city}
                  </p>
                </div>
              </div>

              {/* Content */}
              <ScrollArea className="flex-1 px-6 py-5" style={{ maxHeight: "60vh" }}>
                <div className="space-y-4 max-w-2xl">
                  <div className="flex items-center gap-2">
                    <ClipboardList className="h-4 w-4 text-primary" />
                    <h4 className="text-[13px] font-bold text-foreground">System-Generated Materials</h4>
                    <span className="text-[10px] text-muted-foreground ml-auto">{materials.length} items</span>
                  </div>

                  <div className="space-y-1.5">
                    {materials.map((m, i) => (
                      <div key={i} className="flex items-center justify-between py-2.5 px-4 rounded-lg bg-card border border-border">
                        <div>
                          <p className="text-[12px] font-semibold text-foreground">{m.name}</p>
                          <p className="text-[10px] text-muted-foreground">{m.sku} • {m.category}</p>
                        </div>
                        <div className="flex items-center gap-2">
                          <Badge variant="outline" className="text-[10px]">Qty: {m.qty}</Badge>
                          <CheckCircle className="h-3.5 w-3.5 text-success" />
                        </div>
                      </div>
                    ))}
                  </div>

                  {materials.length === 0 && (
                    <p className="text-[11px] text-muted-foreground text-center py-6">No materials derived from build configuration.</p>
                  )}

                  <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-muted/20 border border-border/40">
                    <span className="text-[10px] font-semibold text-muted-foreground">Source: POS Work Order</span>
                    <span className="text-[10px] text-muted-foreground/50">•</span>
                    <span className="text-[10px] font-semibold text-success">Build Review Approved ✓</span>
                  </div>

                  <p className="text-[10px] text-muted-foreground italic">
                    Materials are read-only and auto-generated from the Sales build. Confirm to proceed to ordering.
                  </p>
                </div>
              </ScrollArea>

              {/* Footer */}
              <div className="px-6 py-4 border-t border-border flex gap-3">
                <Button variant="outline" className="h-10 text-[11px] px-6" onClick={() => setReviewJobId(null)}>
                  Cancel
                </Button>
                <Button className="flex-1 h-10 text-[12px] font-bold gap-1.5" onClick={() => handleConfirmPlanning(reviewJob.id)}>
                  <CheckCircle className="h-3.5 w-3.5" /> Confirm Materials — Ready for Ordering
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
